/**
 * Hours are formatted in exactly one place. The API always returns seconds, and
 * mixing formats across the screen is how a dashboard stops being trustworthy.
 *
 * Decimal hours, one digit: compact enough for a 20-column matrix, and it matches
 * how time is discussed when money enters the picture ("8.5 hours at rate X").
 */
export function formatHours(seconds: number): string {
  if (seconds === 0) return '—';
  const hours = seconds / 3600;
  // Anything under 3 minutes would render as 0,0 and look like nothing at all.
  if (hours < 0.05) return '<0,1';
  return hours.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * Totals are read as magnitudes, so whole hours — except below one hour, where
 * rounding would print a bare "0" next to a row that does contain time.
 */
export function formatHoursTotal(seconds: number): string {
  if (seconds === 0) return '—';
  if (seconds < 3600) return formatHours(seconds);
  return Math.round(seconds / 3600).toLocaleString('ru-RU');
}

/** «в 1 задаче» / «в 12 задачах» — the wrong ending reads as a bug in the numbers. */
export function issuesPlural(count: number): string {
  return count % 10 === 1 && count % 100 !== 11 ? 'задаче' : 'задачах';
}

/** «1 задача» / «2 задачи» / «5 задач» — именительный падеж, когда число впереди. */
export function issuesCountPlural(count: number): string {
  const tens = count % 100;
  if (tens >= 11 && tens <= 14) return 'задач';
  const ones = count % 10;
  if (ones === 1) return 'задача';
  if (ones >= 2 && ones <= 4) return 'задачи';
  return 'задач';
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Последний день периода — тот, который человек считает концом месяца
 * (замечание заказчика 2026-10-21).
 *
 * Внутри периоды полуоткрытые `[from, to)`, и сентябрь — это
 * `2026-09-01 … 2026-10-01`. На экране такое читается как «с первого сентября
 * по первое октября», то есть захватывает лишний день и вводит в заблуждение.
 * Поэтому наружу показываем `30.09.2026`, а в запрос уходит прежняя граница:
 * меняется подпись, а не арифметика.
 */
export function lastDay(isoExclusive: string): string {
  const date = new Date(isoExclusive);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

/** Обратное: из выбранного человеком последнего дня — граница запроса. */
export function nextDay(isoInclusive: string): string {
  const date = new Date(isoInclusive);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

/** Период словами, как его читает человек: `01.09.2026 — 30.09.2026`. */
export function formatPeriod(from: string, toExclusive: string): string {
  return `${formatDate(from)} — ${formatDate(lastDay(toExclusive))}`;
}

/** "3 часа назад" reads faster than a timestamp when judging data freshness. */
export function formatAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diffMs / 60_000);

  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${minutes} мин назад`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;

  const days = Math.round(hours / 24);
  return `${days} дн назад`;
}
